import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { studentsManagement } from "../../hooks/api/StudentsManagement";
import { prepareSelect } from "../../services/core/Select";
import { useTranslation } from "react-i18next";
import Divider from "../../themes/Default/components/Divider/Divider";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { callsManagement } from "../../hooks/api/CallsManagement";
import { locationsManagement } from "../../hooks/api/LocationsManagement";
import { settingsCallManagement } from "../../hooks/api/SettingsCallManagement";
import { Textarea } from "../../themes/Default/components/Form/components/Textarea";
import { languagesManagement } from "../../hooks/api/LanguagesManagement/LanguagesManagement";

const EditCall = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { callId } = useParams();
    const [studentId, setStudentId] = useState();
    const [formErrors, setFormErrors] = useState([]);
    const { useUpdateCallMutation, useViewCallMutation } = callsManagement;
    const [updateCall] = useUpdateCallMutation();
    const [viewCall] = useViewCallMutation();

    const [locations, setLocations] = useState([]);
    const { useLocationListMutation } = locationsManagement;
    const [locationsList] = useLocationListMutation();

    const [studentStatus, setStudentStatus] = useState([]);
    const { useStudentStatusListMutation } = settingsCallManagement;
    const [studentStatusList] = useStudentStatusListMutation();

    const [status, setStatus] = useState([]);
    const { useStatusListMutation } = settingsCallManagement;
    const [statusList] = useStatusListMutation();

    const [courseType, setCourseType] = useState([]);
    const { useCourseTypeListMutation } = settingsCallManagement;
    const [courseTypeList] = useCourseTypeListMutation();

    const [appointment, setAppointment] = useState([]);
    const { useAppointmentListMutation } = settingsCallManagement;
    const [appointmentList] = useAppointmentListMutation();

    const [salesChannel, setSalesChannel] = useState([]);
    const { useSalesChannelListMutation } = settingsCallManagement;
    const [salesChannelList] = useSalesChannelListMutation();

    const [methodOfContact, setMethodOfContact] = useState([]);
    const { useMethodOfContactListMutation } = settingsCallManagement;
    const [methodOfContactList] = useMethodOfContactListMutation();

    const [students, setStudents] = useState([]);
    const { useStudentsListMutation } = studentsManagement;
    const [studentsList] = useStudentsListMutation();

    const [offerSent, setOfferSent] = useState([]);
    const { useOfferSentListMutation } = settingsCallManagement;
    const [offerSentList] = useOfferSentListMutation();

    const [language, setLanguage] = useState([]);
    const { useLanguageListMutation } = languagesManagement;
    const [languageList] = useLanguageListMutation();

    const tabs = [
        { title: t("details"), isActive: true },
    ];

    const gotoAddStudent = () => {
        navigate("/calls/" + callId + "/add-student");
    };

    const gotoEditStudent = () => {
        navigate("/students/edit/" + studentId);
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updateCall({ body, callId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            }else{
                toast.info(result.data.info.responseMessage);
            }
        });
    };

    useEffect(() => {
        Promise.all([
          viewCall(callId),
          locationsList(),
          studentStatusList(),
          statusList(),
          courseTypeList(),
          appointmentList(),
          salesChannelList(),
          methodOfContactList(),
          studentsList(),
          offerSentList(),
          languageList()
        ]).then(([callRes, locationsRes, studentStatusRes, statusRes, courseTypeRes, appointmentRes, salesChannelRes, methodOfContactRes,  studentsRes, offerSentRes, languageRes]) => {
          
          formRef.current.setValues(callRes?.data);
          setStudentId(callRes?.data?.student);
      
          setLocations(prepareSelect(locationsRes?.data?.records.map(l => ({ id: l.id, name: l.name }))));
          setStudentStatus(prepareSelect(studentStatusRes?.data?.records.map(s => ({ id: s.id, name: s.status }))));
          setStatus(prepareSelect(statusRes?.data?.records.map(s => ({ id: s.id, name: s.status }))));
          setCourseType(prepareSelect(courseTypeRes?.data?.records.map(c => ({ id: c.id, name: c.name }))));
          setAppointment(prepareSelect(appointmentRes?.data?.records.map(a => ({ id: a.id, name: a.name }))));
          setSalesChannel(prepareSelect(salesChannelRes?.data?.records.map(s => ({ id: s.id, name: s.name }))));
          setMethodOfContact(prepareSelect(methodOfContactRes?.data?.records.map(m => ({ id: m.id, name: m.name }))));
          setOfferSent(prepareSelect(offerSentRes?.data?.records.map(o => ({ id: o.id, name: o.name }))));
          setLanguage(prepareSelect(languageRes?.data?.records.map(l => ({ id: l.id, name: l.name }))));
          setStudents(prepareSelect(studentsRes?.data?.records.map(st => ({
            id: st.id,
            name: st.firstName + ' ' + st.lastName + ' ' + st.dateOfBirth,
          }))));
        });
      }, [callId])

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/calls" />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
                {studentId ? (
                    <Button title={t("calls.viewStudent")}  icon="fa-regular fa-eye" onClick={gotoEditStudent} />
                ) : (
                    <Button title={t("calls.addStudent")} icon="fa-regular fa-pen" onClick={gotoAddStudent} />
                )}
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("calls.title")} icon="fa-phone" />
                    <Link title={t("calls.edit")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        firstName: "",
                        lastName: "",
                        dateOfBirth: "",
                        note: "",
                        grade: "",
                        guardian1FirstName: "",
                        guardian1LastName: "",
                        guardian1PhoneNumber: "",
                        guardian1Email: "",
                        date: "",
                        studentStatus: "",
                        status: "",
                        courseType: "",
                        appointment: "",
                        location: "",
                        salesChannel: "",
                        methodOfContact: "",
                        offerSent: "",
                        language: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="student"
                                        label={t("calls.student")}
                                        options={students}
                                        onChange={handleChange}
                                        selectedValue={values.student}
                                        error={formErrors?.student}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <DatePicker
                                        name="date"
                                        label={t("test.date")}
                                        required={true}
                                        value={values.date}
                                        onChange={handleChange}
                                        error={formErrors?.date}
                                    />
                                </Field>
                            </Row>
                            <Divider title={t("students.guardian1")} />
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian1FirstName"
                                        label={t("employees.firstName")}
                                        value={values.guardian1FirstName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1FirstName}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="guardian1LastName"
                                        label={t("employees.lastName")}
                                        value={values.guardian1LastName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1LastName}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian1Email"
                                        label={t("employees.email")}
                                        value={values.guardian1Email}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1Email}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="guardian1PhoneNumber"
                                        label={t("employees.phoneNumber")}
                                        required={true}
                                        value={values.guardian1PhoneNumber}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1PhoneNumber}
                                    />
                                </Field>
                            </Row>
                            <Divider title={t("calls.call")} />
                            <Row>
                                <Field>
                                    <Select
                                        name="status"
                                        label={t("status")}
                                        required={true}
                                        options={status}
                                        onChange={handleChange}
                                        selectedValue={values.status}
                                        error={formErrors?.status}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="language"
                                        label={t("program.language")}
                                        options={language}
                                        onChange={handleChange}
                                        selectedValue={values.language}
                                        error={formErrors?.language}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="firstName"
                                        label={t("students.firstName")}
                                        value={values.firstName}
                                        onChange={handleChange}
                                        error={formErrors?.firstName}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="lastName"
                                        label={t("students.lastName")}
                                        value={values.lastName}
                                        onChange={handleChange}
                                        error={formErrors?.lastName}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="dateOfBirth"
                                        label={t("students.dateOfBirth")}
                                        value={values.dateOfBirth ?? ""}
                                        onChange={handleChange}
                                        error={formErrors?.dateOfBirth}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="grade"
                                        label={t("students.grade")}
                                        value={values.grade}
                                        onChange={handleChange}
                                        error={formErrors?.grade}
                                    />
                                </Field>
                            </Row>
                            {/* <Row>
                                <Field>
                                    <Select
                                        name="salesChannel"
                                        label={t("calls.saleChannel")}
                                        required={true}
                                        options={salesChannel}
                                        onChange={handleChange}
                                        selectedValue={values.salesChannel}
                                        error={formErrors?.salesChannel}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="offerSent"
                                        label={t("calls.offerSent")}
                                        required={true}
                                        options={offerSent}
                                        onChange={handleChange}
                                        selectedValue={values.offerSent}
                                        error={formErrors?.offerSent}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row> */}
                            <Row>
                                <Field>
                                    <Select
                                        name="appointment"
                                        label={t("groups.classStartTime")}
                                        options={appointment}
                                        onChange={handleChange}
                                        selectedValue={values.appointment}
                                        error={formErrors?.appointment}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="location"
                                        label={t("groups.location")}
                                        options={locations}
                                        onChange={handleChange}
                                        selectedValue={values.location}
                                        error={formErrors?.location}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="studentStatus"
                                        label={t("calls.old/new")}
                                        required={true}
                                        options={studentStatus}
                                        onChange={handleChange}
                                        selectedValue={values.studentStatus}
                                        error={formErrors?.studentStatus}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="methodOfContact"
                                        label={t("calls.methodOfContacts")}
                                        required={true}
                                        options={methodOfContact}
                                        onChange={handleChange}
                                        selectedValue={values.methodOfContact}
                                        error={formErrors?.methodOfContact}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            {/* <Row>
                                <Field>
                                    <Select
                                        name="courseType"
                                        label={t("students.contractGroupType")}
                                        required={true}
                                        options={courseType}
                                        onChange={handleChange}
                                        selectedValue={values.courseType}
                                        error={formErrors?.courseType}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row> */}
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        name="note"
                                        label={t("calendar.note")}
                                        onChange={handleChange}
                                        value={values.note}
                                        rows={10}
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditCall;
